import { useState, useEffect, useRef, useCallback } from 'react';
import { buildApiUrl } from './api';

/**
 * usePincodeCheck
 * Calls GET /api/v1/customer/orders/check-serviceability?pincode=XXXXXX
 * Debounces by 500ms. Only fires when pincode is exactly 6 digits.
 *
 * Returns: { status: 'idle'|'checking'|'serviceable'|'not_serviceable'|'error', message, checkNow }
 */
const usePincodeCheck = (pincode) => {
  const [status, setStatus] = useState('idle');   // idle | checking | serviceable | not_serviceable | error
  const [message, setMessage] = useState('');
  const timerRef = useRef(null);
  const lastCheckedRef = useRef('');

  const check = useCallback(async (pin) => {
    if (!pin || pin.length !== 6 || /\D/.test(pin)) {
      setStatus('idle');
      setMessage('');
      return;
    }
    // Skip re-checking same pincode
    if (lastCheckedRef.current === pin && status !== 'idle') return;

    lastCheckedRef.current = pin;
    setStatus('checking');
    setMessage('Checking delivery availability...');

    try {
      const url = buildApiUrl(`api/v1/customer/orders/check-serviceability?pincode=${pin}`);
      console.log('Checking serviceability for pincode:', pin, 'URL:', url);
      
      const res = await fetch(url);
      console.log('Response status:', res.status);
      
      let json;
      try {
        json = await res.json();
        console.log('Response JSON:', json);
      } catch (parseError) {
        console.error('Failed to parse JSON:', parseError);
        throw new Error('Invalid response from server');
      }

      if (!res.ok) throw new Error(json.message || 'Failed to check serviceability');

      const serviceable = json.data?.serviceable ?? json.serviceable ?? false;
      console.log('Serviceability result:', serviceable);
      setStatus(serviceable ? 'serviceable' : 'not_serviceable');
      setMessage(json.message || (serviceable ? 'Delivery available' : 'Delivery not available'));
    } catch (err) {
      console.error('Pincode check error:', err);
      setStatus('error');
      setMessage(err.message || 'Unable to verify pincode. Please try again.');
    }
  }, [status]);

  // Auto-check with debounce whenever pincode changes
  useEffect(() => {
    clearTimeout(timerRef.current);

    if (!pincode || pincode.length !== 6) {
      setStatus('idle');
      setMessage('');
      lastCheckedRef.current = '';
      return;
    }

    timerRef.current = setTimeout(() => check(pincode), 500);
    return () => clearTimeout(timerRef.current);
  }, [pincode, check]);

  return { status, message, checkNow: check };
};

export default usePincodeCheck;
